import React, { Component } from 'react';
import {
  bool, func
} from 'prop-types';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { Link } from 'react-router-dom';
import { surveyOptionsType, surveyTypeValues } from '../../../constants/types';
import { DEGREE_PROGRAM_NOT_SELECTED, routes, surveyFieldIds } from '../../../constants';
import startSurveyStyles from '../startSurvey.module.css';
import { getTranslation, langType, withLang } from '../../../i18n';
import FormNavigation from '../FormNavigation';
import WithValidation from '../../WithValidation';
import { contentsPageValidator } from '../../../validators/surveys';
import { LocalizedInput, LocalizedRichTextArea } from '../../LocalizedInput';
import TextInput from '../../TextInput';
import IsRequiredField from '../../IsRequiredField';
import {
  addErrorMessageAction, setTaskFulfilled, setTaskPending, updateSurveyOptionsAction
} from '../../../actions';
import { getDegreePrograms } from '../../../api/degreePrograms';
import { getSurveySchema } from '../../../api';
import styles from './contents.module.css';
import { getCompiledPath, getSubRouteForSurveyType, isSurveyForDegreeProgram } from '../../../util';
import withRouter from '../../WithRouter';
import { Select } from '../../Select';

class Contents extends Component {
  constructor(props) {
    super(props);
    this.renderDegreeProgramSelect = this.renderDegreeProgramSelect.bind(this);
    this.renderExtraSurveyParts = this.renderExtraSurveyParts.bind(this);
    this.renderDefaultSurveyTitleInputs = this.renderDefaultSurveyTitleInputs.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onLocalizedInputChange = this.onLocalizedInputChange.bind(this);
    this.onLocalizedRTEChange = this.onLocalizedRTEChange.bind(this);
    this.onRemoveExtraSurveyPart = this.onRemoveExtraSurveyPart.bind(this);
    this.onSelectDegreeProgram = this.onSelectDegreeProgram.bind(this);
    this.renderSurveyPartRow = this.renderSurveyPartRow.bind(this);
    this.surveyTypeRoute = getSubRouteForSurveyType(props.surveyOptions.surveyType);

    this.state = {
      degreePrograms: [],
      surveySchemaParts: [],
      schemaExtraSurveyPart: undefined
    };
  }

  async componentDidMount() {
    const {
      surveyOptions,
      dispatchSetTaskPending,
      dispatchSetTaskFulfilled,
      dispatchAddErrorMessage
    } = this.props;

    if (!surveyOptions || !surveyOptions.schemaId) {
      this.props.navigate(routes.surveys.index, { replace: true });
    } else {
      dispatchSetTaskPending();
      try {
        const schema = await getSurveySchema(surveyOptions.schemaId);

        this.setState({
          surveySchemaParts: schema.surveySchemaParts,
          schemaExtraSurveyPart: schema.extraSurveyPart
        });
        // Fetch degree programs but don't wait for them
        if (isSurveyForDegreeProgram(surveyOptions.surveyType)) {
          this.fetchDegreePrograms();
        }
      } catch (e) {
        dispatchAddErrorMessage(e);
      } finally {
        dispatchSetTaskFulfilled();
      }
    }
  }

  onInputChange(event) {
    this.props.dispatchUpdate({ [event.target.id]: event.target.value });
  }

  onTextInputChange(lang) {
    return (event) => {
      this.props.dispatchUpdate({ [event.target.id]: { [lang]: event.target.value } });
    };
  }

  onLocalizedInputChange(lang) {
    return (event) =>
      this.props.dispatchUpdate({
        [event.target.name]: { [lang]: event.target.value }
      });
  }

  onLocalizedRTEChange(lang, name) {
    return (value) => this.props.dispatchUpdate({ [name]: { [lang]: value } });
  }

  onRemoveExtraSurveyPart() {
    this.props.dispatchUpdate({ extraSurveyPart: null });
  }

  onSelectDegreeProgram(value) {
    const degreeProgramCode = value;
    const degreeProgram = degreeProgramCode !== DEGREE_PROGRAM_NOT_SELECTED.code
      ? this.state.degreePrograms.find((dp) => dp.code === degreeProgramCode)
      : DEGREE_PROGRAM_NOT_SELECTED;
    this.props.dispatchUpdate({ degreeProgram });
  }

  fetchDegreePrograms = async () => {
    const { dispatchAddErrorMessage } = this.props;
    try {
      const degreePrograms = await getDegreePrograms();
      if (degreePrograms.error) {
        dispatchAddErrorMessage(degreePrograms.error);
      }
      this.setState({ degreePrograms });
    } catch (e) {
      dispatchAddErrorMessage(e);
    }
  };

  renderDegreeProgramSelect() {
    const { degreePrograms } = this.state;
    const {
      surveyOptions,
      showProblems,
      hasInvalidField,
      lang, translate: t
    } = this.props;
    const isLoading = !this.state.degreePrograms.length;
    const sorting = (a, b) => getTranslation(a.name, lang).localeCompare(getTranslation(b.name, lang));
    const sortedDpOptions = degreePrograms.toSorted(sorting)
      .map((dp) => ({
        value: dp.code,
        label: `${dp.code} - ${getTranslation(dp.name, lang)}`
      }));

    const options = isLoading
      ? [{ value: '', label: t('surveyParticipantsEditor.loading') }]
      : [
        { value: DEGREE_PROGRAM_NOT_SELECTED.code, label: t('startSurvey.degreeProgramNotSelected') },
        ...sortedDpOptions
      ];

    return (
      <Select
        options={options}
        value={surveyOptions.degreeProgram?.code}
        className={showProblems && hasInvalidField(surveyFieldIds.DEGREE_PROGRAM_CODE) ? 'formError formContainer' : ''}
        placeholderKey="startSurvey.selectDegreeProgram"
        onChange={this.onSelectDegreeProgram}
        dataCy="degreeProgramSelectDropdown"
        id={surveyFieldIds.DEGREE_PROGRAM_CODE}
      />
    );
  }

  renderSurveyConclusion() {
    const { surveyOptions } = this.props;

    switch (surveyOptions.surveyType) {
      case surveyTypeValues.COURSE_SURVEY:
        return this.renderDefaultSurveyConclusion();
      default:
        return this.renderDegreeProgrammeSurveyConclusion();
    }
  }

  renderDegreeProgrammeSurveyConclusion() {
    const { translate: t } = this.props;
    return (
      <>
        <h2 className="panel__title">5. {t('startSurvey.completion')}</h2>
        <p><Translate id="startSurvey.conclusionDescription" /></p>
        <div className={startSurveyStyles.formItem}>
          <LocalizedRichTextArea
            name={surveyFieldIds.CONCLUSION}
            onInputChange={this.onLocalizedRTEChange}
            values={this.props.surveyOptions[surveyFieldIds.CONCLUSION] || {}}
            placeholderTranslationNamespace="inLanguage"
            useAccordion
          />
        </div>
      </>
    );
  }

  renderDefaultSurveyConclusion() {
    const { translate: t } = this.props;
    return (
      <>
        <h2 className="panel__title">3. {t('startSurvey.completion')}</h2>
        <p><Translate id="startSurvey.conclusionDescription" /></p>
        <div className={startSurveyStyles.formItem}>
          <LocalizedRichTextArea
            name={surveyFieldIds.CONCLUSION}
            onInputChange={this.onLocalizedRTEChange}
            values={this.props.surveyOptions[surveyFieldIds.CONCLUSION] || {}}
            placeholderTranslationNamespace="inLanguage"
            useAccordion
          />
        </div>
      </>
    );
  }

  renderSurveyPartRow(part, enableRemove) {
    const { lang, translate: t } = this.props;
    return (
      <tr key={part.id} className={styles.row}>
        <td>
          {getTranslation(part.title, lang) || t('surveyParts.titleMissing')}
        </td>
        <td>
          {part.code}
        </td>
        <td>
          {getTranslation(part.description, lang) || t('surveyParts.descriptionMissing')}
        </td>
        {!enableRemove ? <td /> : (
          <td>
            <button
              type="button"
              onClick={this.onRemoveExtraSurveyPart}
              className="icon--trash iconButton"
              title={t('common.remove')}
              data-cy="remove-extra-survey-part-button"
            />
          </td>
        )}
      </tr>
    );
  }

  renderSurveyParts() {
    const { surveySchemaParts, schemaExtraSurveyPart } = this.state;

    return (
      <table className={styles.extraSurveyPartItem}>
        <thead>
          <tr className={styles.header}>
            <th><Translate id="surveyParts.name" /></th>
            <th><Translate id="surveyParts.identifier" /></th>
            <th><Translate id="common.description" /></th>
            <th />
          </tr>
        </thead>
        <tbody>
          {surveySchemaParts.map((surveySchemaPart) => this.renderSurveyPartRow(surveySchemaPart, false))}
          {schemaExtraSurveyPart && this.renderSurveyPartRow(schemaExtraSurveyPart, false)}
        </tbody>
      </table>
    );
  }

  renderExtraSurveyPart() {
    const { surveyOptions } = this.props;

    return (
      surveyOptions.extraSurveyPart ? (
        <table className={styles.extraSurveyPartItem}>
          <thead>
            <tr className={styles.header}>
              <th><Translate id="surveyParts.name" /></th>
              <th><Translate id="surveyParts.identifier" /></th>
              <th><Translate id="common.description" /></th>
              <th />
            </tr>
          </thead>
          <tbody>
            {this.renderSurveyPartRow(surveyOptions.extraSurveyPart, true)}
          </tbody>
        </table>
      ) : null
    );
  }

  renderDegreeProgramSurveyTitleInputs() {
    const {
      surveyOptions, showProblems, hasInvalidField, lang
    } = this.props;
    const titleContainerClass = showProblems && hasInvalidField(surveyFieldIds.TITLE)
      ? `${startSurveyStyles.formItem} formError`
      : startSurveyStyles.formItem;

    return (
      <>
        <div className={startSurveyStyles.formItem}>
          <label htmlFor={surveyFieldIds.DEGREE_PROGRAM_CODE}>
            <Translate id="common.degreeProgram" />
            {surveyOptions.surveyType === surveyTypeValues.DEGREE_PROGRAM_SURVEY && <IsRequiredField />}
          </label>
          <p className={`form__description ${styles.inputDescription}`}>
            <Translate id="startSurvey.degreeProgramDescription" />
          </p>
          {this.renderDegreeProgramSelect()}
        </div>

        <div className={titleContainerClass}>
          <label htmlFor={surveyFieldIds.TITLE}>
            <Translate id="startSurvey.name" />
            <IsRequiredField />
          </label>
          <p><Translate id="startSurvey.nameDescription.degreeProgramSurvey" /></p>

          <span className={styles.degreeProgramSurveyTitleStaticPart}>
            {getTranslation(surveyOptions.schemaTitle, lang)}
          </span>
          {surveyOptions.degreeProgram?.name && (
            <span className={styles.degreeProgramSurveyTitleStaticPart}>
              {getTranslation(surveyOptions.degreeProgram.name, lang)}
            </span>
          )}
          {surveyOptions.titlePeriods && (
            <select
              className={styles.degreeProgramSurveyTitlePeriod}
              id="selectedTitlePeriod"
              value={surveyOptions.selectedTitlePeriod}
              onChange={this.onInputChange}
            >
              {surveyOptions.titlePeriods.map((period, idx) => (
                <option
                  key={getTranslation(period, lang)}
                  value={idx}
                >
                  {getTranslation(period, lang)}
                </option>
              ))}
            </select>
          )}
          <input
            className={styles.degreeProgramSurveyTitleExtraPart}
            type="text"
            id="titleExtraPart"
            value={surveyOptions.titleExtraPart}
            onChange={this.onInputChange}
            data-cy="degree-program-survey-title-extra-part-input"
          />
        </div>
      </>
    );
  }

  renderDefaultSurveyTitleInputs() {
    const {
      surveyOptions, showProblems, hasInvalidField
    } = this.props;
    const titleContainerClass = showProblems && hasInvalidField(surveyFieldIds.TITLE)
      ? `${startSurveyStyles.formItem} formError`
      : startSurveyStyles.formItem;

    return (
      <div className={titleContainerClass}>
        <label htmlFor={surveyFieldIds.TITLE}>
          <Translate id="startSurvey.name" />
          <IsRequiredField />
        </label>
        <p><Translate id="startSurvey.nameDescription.defaultSurvey" /></p>
        <LocalizedInput
          name={surveyFieldIds.TITLE}
          onInputChange={this.onLocalizedInputChange}
          values={surveyOptions[surveyFieldIds.TITLE] || {}}
          placeholderTranslationNamespace="inLanguage"
          useAccordion
          maxLength={255}
          dataCyPrefix="survey-title-input"
        />
      </div>
    );
  }

  renderSurveyTitleInputs() {
    const { surveyOptions } = this.props;

    if (isSurveyForDegreeProgram(surveyOptions.surveyType)) {
      return this.renderDegreeProgramSurveyTitleInputs();
    }

    return this.renderDefaultSurveyTitleInputs();
  }

  renderExtraSurveyParts() {
    const { surveyOptions } = this.props;

    if (surveyOptions.surveyType === surveyTypeValues.COURSE_SURVEY) {
      return null;
    }
    // surveyTypeValues.DEGREE_PROGRAM_SURVEY || surveyTypeValues.HUP
    return this.renderDegreeProgramExtraSurveyParts();
  }

  renderDegreeProgramExtraSurveyParts() {
    const { translate: t, surveyOptions } = this.props;

    return (
      <>
        <div className={startSurveyStyles.formItem}>
          <h2 className="panel__title">3. {t('startSurvey.surveyParts.title')}</h2>
          <p><Translate id="startSurvey.surveyParts.introduction" /></p>
          {this.renderSurveyParts()}
        </div>
        <div className={startSurveyStyles.formItem}>
          <h2 className="panel__title">4. {t('startSurvey.extraSurveyPart.title')}</h2>
          <p><Translate id="startSurvey.extraSurveyPart.introduction" /></p>
          {this.renderExtraSurveyPart()}
          <div className={styles.questionsActions}>
            <Link
              className={`button button--action icon--plus ${surveyOptions.extraSurveyPart ? 'disabled' : ''}`}
              to={surveyOptions.id
                ? getCompiledPath(this.surveyTypeRoute.survey.edit.contents.extraSurveyPart, { surveyId: surveyOptions.id })
                : this.surveyTypeRoute.startSurvey.contents.extraSurveyPart}
              data-cy="choose-extra-survey-parts-button"
            >
              <Translate id="startSurvey.extraSurveyPart.add" />
            </Link>
          </div>
          <br />
          <div className={styles.questionsActions}>
            <Link
              className="button button--action"
              to={surveyOptions.id
                ? getCompiledPath(this.surveyTypeRoute.survey.edit.contents.questions, { surveyId: surveyOptions.id })
                : this.surveyTypeRoute.startSurvey.contents.questions}
            >
              <Translate id="startSurvey.viewQuestions.view" />
            </Link>
          </div>
        </div>
      </>
    );
  }

  render() {
    const {
      surveyOptions, showProblems, translate: t, hasInvalidField
    } = this.props;

    const formRoutes = {
      next: surveyOptions.id
        ? getCompiledPath(this.surveyTypeRoute.survey.edit.messages, { surveyId: surveyOptions.id })
        : this.surveyTypeRoute.startSurvey.messages
    };

    return (
      <section>
        <form className="form">
          <h2 className="panel__title" id={`${surveyFieldIds.TITLE}_heading`}>1. {t('startSurvey.basicInfo')}</h2>
          {this.renderSurveyTitleInputs()}

          <div className={startSurveyStyles.formItem}>
            <label htmlFor={surveyFieldIds.DESCRIPTION}>
              <Translate id="common.description" />
            </label>
            <TextInput
              id={surveyFieldIds.DESCRIPTION}
              type="text"
              defaultValue={getTranslation(surveyOptions.description, this.props.lang)}
              onChange={this.onTextInputChange(this.props.lang)}
              className={showProblems && hasInvalidField(surveyFieldIds.DESCRIPTION) ? 'formError' : ''}
              maxLength={1024}
              dataCy="survey-description-input"
            />
          </div>

          <h2 className="panel__title">2. {t('startSurvey.introduction')}</h2>
          <p><Translate id="startSurvey.introductionDescription" /></p>
          <div className={startSurveyStyles.formItem}>
            <LocalizedRichTextArea
              name={surveyFieldIds.INTRODUCTION}
              onInputChange={this.onLocalizedRTEChange}
              values={this.props.surveyOptions[surveyFieldIds.INTRODUCTION] || {}}
              placeholderTranslationNamespace="inLanguage"
              useAccordion
            />
          </div>
          { this.renderExtraSurveyParts() }
          { this.renderSurveyConclusion() }
          <FormNavigation
            canProceed={this.props.isValid}
            next={formRoutes.next}
            showProblemsFunc={this.props.jumpToProblems}
          />
        </form>
      </section>
    );
  }
}

Contents.propTypes = {
  surveyOptions: surveyOptionsType.isRequired,
  dispatchUpdate: func.isRequired,
  isValid: bool.isRequired,
  showProblems: bool.isRequired,
  hasInvalidField: func.isRequired,
  jumpToProblems: func.isRequired,
  dispatchAddErrorMessage: func.isRequired,
  dispatchSetTaskPending: func.isRequired,
  dispatchSetTaskFulfilled: func.isRequired,
  navigate: func.isRequired,
  ...langType
};

const mapStateToProps = ({ survey }) => ({
  surveyOptions: survey.surveyOptions
});

const mapDispatchToProps = (dispatch) => ({
  dispatchUpdate: (surveyOptions) =>
    dispatch(updateSurveyOptionsAction(surveyOptions)),
  dispatchAddErrorMessage: (err) => dispatch(addErrorMessageAction(err)),
  dispatchSetTaskPending: () => dispatch(setTaskPending()),
  dispatchSetTaskFulfilled: () => dispatch(setTaskFulfilled())
});

export default withLang(withRouter(connect(mapStateToProps, mapDispatchToProps)(WithValidation(Contents,
  contentsPageValidator))));
